import axios from "axios";
import { createContext, useContext, useEffect, useState } from "react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Silently refresh the access cookie once on a 401, then retry the request.
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    if (status === 401 && original && !original._retry && !url.includes("/auth/")) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(error);
  }
);

export function fmtErr(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setUser(r.data);
      } catch (e) {
        try {
          await api.post("/auth/refresh");
          const r2 = await api.get("/auth/me");
          setUser(r2.data);
        } catch (e2) {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };
  const register = async (name, email, password, ref) => {
    const { data } = await api.post("/auth/register", { name, email, password, ref });
    setUser(data.user);
    return data.user;
  };
  const logout = async () => { try { await api.post("/auth/logout"); } catch (e) { /* noop */ } setUser(null); };
  const refreshUser = async () => { const r = await api.get("/auth/me"); setUser(r.data); return r.data; };

  return <AuthCtx.Provider value={{ user, setUser, loading, login, register, logout, refreshUser }}>{children}</AuthCtx.Provider>;
}
